import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { CustomFileUpload, CustomToggle } from '../Components/CustomFormElements';
import { ArtifactApiProvider, useArtifactApi } from '../Services/ArtifactApiProvider';

const defaultFormats = ['image/png', 'image/jpeg', 'image/heic', 'application/pdf'];

function ArtifactBatchCreate() {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { createArtifact } = useArtifactApi();

  const [files, setFiles] = useState([]);
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);

  const onFileSelect = (selectedFiles) => {
    setFiles(selectedFiles);
  };

  const onCreateArtifacts = async () => {
    if (files.length === 0) return;

    setLoading(true);

    const successFiles = [];
    const failedFiles = [];
    const conflictFiles = [];
    for (const file of files) {
      const fileName = file.name;

      const formData = new FormData();
      formData.append('name', fileName);
      formData.append('type', 'default');
      formData.append('is_active', 'true');
      formData.append('is_public', isPublic ? 'true' : 'false');
      formData.append('file', file);

      try {
        await createArtifact(formData);

        successFiles.push(fileName);
      } catch (error) {
        if (error.response?.status === 409) {
          conflictFiles.push(fileName);
        } else {
          failedFiles.push(fileName);
        }
      }
    }

    const alerts = [
      successFiles.length && {
        type: 'success',
        message: `${successFiles.length} artifacts created successfully!`,
      },
      conflictFiles.length && {
        type: 'fail',
        message: `Artifacts already exist: ${conflictFiles.join(', ')}`,
      },
      failedFiles.length && {
        type: 'fail',
        message: `Failed to create artifacts: ${failedFiles.join(', ')}`,
      },
    ].filter(Boolean);

    setLoading(false);

    navigate(`/organizations/${organizationId}/artifacts`, { state: { alerts } });
  };

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className="h2-title">Batch Create Artifacts</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Artifact file</div>
                  <div className="small-font">Select or replace artifact file</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomFileUpload
                    multiple={true}
                    allowedFormats={defaultFormats}
                    showUploadedFiles={true}
                    allowFileDeletion={true}
                    onFileSelect={onFileSelect}
                    showAlert={showAlert}
                    showConfirmation={showConfirmation}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between mt-3">
                <div className="input-description">
                  <div className="input-title">Artifact private</div>
                  <div className="small-font">Set artifact to be public or private</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="toggle-status-public"
                    label={isPublic ? 'Public' : 'Private'}
                    checked={isPublic}
                    onChange={(id, value) => setIsPublic(value)}
                  />
                </div>
              </div>

              <div className="buttons-container d-flex justify-end gap-2 mt-3">
                <Button
                  variant="primary"
                  size="medium"
                  disabled={files.length === 0 || loading}
                  onClick={onCreateArtifacts}
                >
                  Create {files.length} artifact{files.length !== 1 ? 's' : ''}
                </Button>
                <Button
                  variant="danger"
                  size="medium"
                  onClick={() => navigate(`/organizations/${organizationId}/artifacts`)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ArtifactBatchCreateWrapper() {
  return (
    <ConfirmationProvider>
      <ArtifactApiProvider>
        <ArtifactBatchCreate />
      </ArtifactApiProvider>
    </ConfirmationProvider>
  );
}

export default ArtifactBatchCreateWrapper;
